import { useEffect, type FC } from 'react'
import { Script } from 'gatsby'

import { debugLog } from 'lib/analytics'
import { getShopifyTrackingConsent, shopifyConsentMatches } from 'lib/consent'

// https://shopify.dev/docs/api/customer-privacy#installation-on-a-custom-storefront
const SCRIPT_SRC =
  'https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.1/consent-tracking-api.js'

// Shared root of storefront and checkout, where Shopify sets the consent
// cookie so checkout can read it.
const STOREFRONT_ROOT_DOMAIN = 'okapa.com'

// Records the Cookiebot answer with Shopify's Customer Privacy API so checkout
// and Shopify-managed pixels/apps on checkout.okapa.com honor it. Nothing is
// recorded until the shopper has answered the banner (Shopify requires a
// visitor interaction, so Cookiebot's implied defaults don't count) or when
// Shopify already holds the same answer.
const ShopifyCustomerPrivacy: FC = () => {
  const record = () => {
    const checkoutRootDomain = process.env.GATSBY_SHOPIFY_DOMAIN_V2
    const storefrontAccessToken =
      process.env.GATSBY_SHOPIFY_STOREFRONT_ACCESS_TOKEN_V2
    if (!checkoutRootDomain || !storefrontAccessToken) return
    const api = window.Shopify?.customerPrivacy
    if (!api || !window.Cookiebot?.hasResponse) return
    const consent = getShopifyTrackingConsent()
    if (shopifyConsentMatches(api.currentVisitorConsent(), consent)) return
    api.setTrackingConsent(
      {
        ...consent,
        headlessStorefront: true,
        checkoutRootDomain,
        storefrontRootDomain: STOREFRONT_ROOT_DOMAIN,
        storefrontAccessToken,
      },
      (error) => {
        if (error) {
          debugLog('setTrackingConsent failed', { error: String(error) })
        }
      }
    )
  }

  useEffect(() => {
    window.addEventListener('CookiebotOnAccept', record)
    window.addEventListener('CookiebotOnDecline', record)
    return () => {
      window.removeEventListener('CookiebotOnAccept', record)
      window.removeEventListener('CookiebotOnDecline', record)
    }
  }, [])

  // Known gap in `gatsby develop`: StrictMode double-runs effects, and
  // gatsby-script's cleanup removes the element while its cache keeps the id,
  // so the script never loads locally. Production builds are unaffected;
  // verify the hand-off on a deploy preview.
  return (
    <Script
      id="shopify-customer-privacy"
      src={SCRIPT_SRC}
      strategy="idle"
      onLoad={record}
      onError={() => debugLog('Customer Privacy API failed to load')}
    />
  )
}

export default ShopifyCustomerPrivacy
